import type { AppManifest } from '@yada/types';

function isSaveDataOrSlowConnection(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as any;
  if (nav.connection?.saveData) return true;
  if (nav.connection?.effectiveType && ['slow-2g', '2g'].includes(nav.connection.effectiveType)) return true;
  return false;
}

function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as any;
  const ua = nav.userAgent || '';
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isTouch = typeof nav.maxTouchPoints === 'number' && nav.maxTouchPoints > 1;
  const isNarrow = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 48rem)').matches;
  return mobileRegex.test(ua) || (isTouch && isNarrow);
}

/**
 * Prefetches and warms up dynamic import bundles for all registered apps
 * when the browser is idle and online.
 *
 * This ensures that:
 * 1. App chunks are loaded into memory and browser CacheStorage / HTTP cache.
 * 2. When the user goes offline, any app can be activated immediately without network failure.
 * 3. App transitions are instant with zero load latency.
 */
export function prefetchApps(manifests: AppManifest[], customDelayMs?: number): () => void {
  const g: any = typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null);
  if (!g) return () => {};

  if (isSaveDataOrSlowConnection()) {
    // Respect user data-saver preferences and slow 2G connections
    return () => {};
  }

  const isMobile = isMobileDevice();
  const delayMs = customDelayMs ?? (isMobile ? 25000 : 15000);
  const interAppDelay = isMobile ? 1500 : 800;

  // On mobile, filter out developer-only manifests to conserve RAM and CPU
  const targetManifests = isMobile
    ? manifests.filter(m => !(m as any).developerOnly)
    : manifests;

  let cancelled = false;
  let timeoutId: any = null;
  let idleId: any = null;

  const startPrefetch = () => {
    if (cancelled || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;

    let index = 0;
    const loadNext = () => {
      if (cancelled || index >= targetManifests.length || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;

      const manifest = targetManifests[index++];
      try {
        Promise.resolve(manifest.load())
          .catch((err) => {
            console.debug(`[Prefetch] Skipping ${manifest.id}:`, err);
          })
          .finally(() => {
            if (!cancelled) {
              timeoutId = setTimeout(loadNext, interAppDelay);
            }
          });
      } catch {
        if (!cancelled) {
          loadNext();
        }
      }
    };

    loadNext();
  };

  if (typeof g.requestIdleCallback === 'function') {
    idleId = g.requestIdleCallback(
      () => {
        timeoutId = setTimeout(startPrefetch, delayMs);
      },
      { timeout: 8000 }
    );
  } else {
    timeoutId = setTimeout(startPrefetch, delayMs);
  }

  const onOnline = () => {
    if (!cancelled) {
      startPrefetch();
    }
  };

  if (typeof g.addEventListener === 'function') {
    g.addEventListener('online', onOnline);
  }

  return () => {
    cancelled = true;
    if (timeoutId) clearTimeout(timeoutId);
    if (idleId && typeof g.cancelIdleCallback === 'function') {
      g.cancelIdleCallback(idleId);
    }
    if (typeof g.removeEventListener === 'function') {
      g.removeEventListener('online', onOnline);
    }
  };
}
